"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Appointment, Customer, Vehicle, Staff } from "@/types";

interface UpcomingAppointmentsProps {
  appointments: Appointment[];
  customers: Customer[];
  vehicles: Vehicle[];
  staff: Staff[];
}

function getStatusBadge(status: string) {
  const colors: Record<string, string> = {
    PENDING: "bg-orange-50 text-orange-700 border-orange-100",
    CONFIRMED: "bg-blue-50 text-blue-700 border-blue-100",
    COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-100",
    CANCELLED: "bg-red-50 text-red-700 border-red-100",
    IN_PROGRESS: "bg-yellow-50 text-yellow-700 border-yellow-100",
    NOT_COMPLETED: "bg-pink-50 text-pink-700 border-pink-100",
    POSTPONED: "bg-indigo-50 text-indigo-700 border-indigo-100",
    "confirmation-pending": "bg-orange-50 text-orange-700 border-orange-100",
    confirmed: "bg-blue-50 text-blue-700 border-blue-100",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-100",
    cancelled: "bg-red-50 text-red-700 border-red-100",
    "in-progress": "bg-yellow-50 text-yellow-700 border-yellow-100",
    "not-completed": "bg-pink-50 text-pink-700 border-pink-100",
    postponed: "bg-indigo-50 text-indigo-700 border-indigo-100",
  };

  return (
    <Badge variant="outline" className={colors[status] || ""}>
      {status}
    </Badge>
  );
}

export function UpcomingAppointments({
  appointments,
  customers,
  vehicles,
  staff,
}: UpcomingAppointmentsProps) {
  const navigate = useNavigate();

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">
              Todays Appointments
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Today's scheduled services for your customers
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/appointments")}
          >
            <Eye className="mr-2 h-4 w-4" />
            View All
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-b hover:bg-transparent">
              <TableHead className="text-xs font-medium">Date</TableHead>
              <TableHead className="text-xs font-medium">Customer</TableHead>
              <TableHead className="text-xs font-medium">Vehicle</TableHead>
              <TableHead className="text-xs font-medium">Type</TableHead>
              <TableHead className="text-xs font-medium">
                Assigned Staff
              </TableHead>
              <TableHead className="text-xs font-medium">Status</TableHead>
              <TableHead className="text-right text-xs font-medium">
                Amount
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appointments.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground py-8"
                >
                  No appointments for today
                </TableCell>
              </TableRow>
            ) : (
              appointments.map((appointment) => {
                const customer = customers.find(
                  (c) => c.id === appointment.customerId
                );
                const matchedVehicles = (appointment.vehicleIds ?? [])
                  .map((vId) => vehicles.find((v) => v.id === vId))
                  .filter(Boolean) as Vehicle[];

                const vehicleLabel =
                  matchedVehicles.length > 0
                    ? matchedVehicles
                        .map((v) =>
                          (v.make || v.model)
                            ? `${v.make ?? ""} ${v.model ?? ""}`.trim()
                            : v.plateNumber ?? "Unknown"
                        )
                        .join(", ")
                    : null;

                const staffMember = staff.find(
                  (s) => s.id === appointment.staffId
                );

                return (
                  <TableRow key={appointment.id} className="border-b">
                    <TableCell>
                      <span className="text-sm font-medium">
                        {formatDate(appointment.date)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {customer?.name || "N/A"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {vehicleLabel || "N/A"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {appointment.type.replace(/_/g, " ")}
                    </TableCell>
                    <TableCell className="text-sm">
                      {staffMember?.name || "Unassigned"}
                    </TableCell>
                    <TableCell>{getStatusBadge(appointment.status)}</TableCell>
                    <TableCell className="text-right text-sm font-medium">
                      {appointment.amount !== null
                        ? formatCurrency(appointment.amount)
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
